import { useTranslation } from 'react-i18next'
import type { VerifyCheckStatus } from '../../utils/verifyReportAdapter'

const LABEL: Record<VerifyCheckStatus, string> = {
  pass: 'vc.imeiReport.chip.ok',
  warning: 'vc.imeiReport.chip.warn',
  fail: 'vc.imeiReport.chip.risk',
  unverified: 'vc.imeiReport.chip.unknown',
  not_applicable: 'vc.imeiReport.chip.muted',
}

function StatusIcon({ status, interactive }: { status: VerifyCheckStatus; interactive: boolean }) {
  if (status === 'not_applicable') return null

  const className = `vr-status__icon${status === 'unverified' && !interactive ? ' vr-status__icon--muted' : ''}`

  if (status === 'pass') {
    return (
      <span className={className} aria-hidden="true">
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path
            d="M2.5 6.2 4.8 8.5 9.5 3.5"
            stroke="currentColor"
            strokeWidth="1.85"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    )
  }

  if (status === 'warning') {
    return (
      <span className={className} aria-hidden="true">
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path
            d="M6 2.5v4.2M6 9.2h.01"
            stroke="currentColor"
            strokeWidth="1.85"
            strokeLinecap="round"
          />
        </svg>
      </span>
    )
  }

  if (status === 'fail') {
    return (
      <span className={className} aria-hidden="true">
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path
            d="m3.2 3.2 5.6 5.6M8.8 3.2l-5.6 5.6"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
          />
        </svg>
      </span>
    )
  }

  if (status === 'unverified') {
    if (interactive) {
      return (
        <span className={className} aria-hidden="true">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path
              d="M3.5 8.5 8.5 3.5M6.5 3.5h2v2"
              stroke="currentColor"
              strokeWidth="1.65"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      )
    }
    return (
      <span className={className} aria-hidden="true">
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <circle cx="6" cy="9" r="0.95" fill="currentColor" />
          <path
            d="M6 3.2v3.6"
            stroke="currentColor"
            strokeWidth="1.85"
            strokeLinecap="round"
          />
        </svg>
      </span>
    )
  }

  return null
}

type Props = {
  status: VerifyCheckStatus
  className?: string
  /** Row is clickable for follow-up check — show affordance on unverified. */
  interactive?: boolean
}

export default function VerifyStatusBadge({ status, className = '', interactive = false }: Props) {
  const { t } = useTranslation()
  return (
    <span
      className={`vr-status vr-status--${status}${interactive ? ' vr-status--interactive' : ''}${className ? ` ${className}` : ''}`}
    >
      <StatusIcon status={status} interactive={interactive} />
      <span className="vr-status__label">{t(LABEL[status])}</span>
    </span>
  )
}
